import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { styles } from './styles';
import {
    usePreCreateRolesMappingToChangeRequestPolicyMutation,
    useSaveRolesMappedToChangeRequestPolicyMutation,
} from '../../../api/preHQ';
import { DisplayChangeRequestPolicyRoles } from '../../changeRequestPolicyRoles';
import PermissionChecker from '../../../components/common/PermissionChecker';

const ChangeRequestPolicyRoles = (props) => {
    const [roles, setRoles] = useState({});
    const [selectedRoles, setSelectedRoles] = useState({
        approver: [],
        reviewer: [],
    });

    const { enqueueSnackbar } = useSnackbar();

    const [preCreateRolesMapping, { isLoading: isPreCreateLoading }] =
        usePreCreateRolesMappingToChangeRequestPolicyMutation();
    const [saveRolesMapped, { isLoading: isSaveLoading }] = useSaveRolesMappedToChangeRequestPolicyMutation();

    const isLoading = isPreCreateLoading || isSaveLoading;

    const fetchPreCreateUpdateData = async () => {
        try {
            const response = await preCreateRolesMapping().unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            setRoles({ reviewer: response?.reviewerRolesList || [], approver: response?.approverRolesList });
            setSelectedRoles({
                approver: response?.approverRoles ? JSON.parse(response?.approverRoles) : [],
                reviewer: response?.reviewerRoles ? JSON.parse(response?.reviewerRoles) : [],
            });
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    useEffect(() => {
        fetchPreCreateUpdateData();
    }, []);

    const updateRoles = async () => {
        const bodyFormData = new FormData();
        bodyFormData.append('approverRoles', selectedRoles.approver);
        bodyFormData.append('reviewerRoles', selectedRoles.reviewer);
        try {
            const response = await saveRolesMapped(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response?.message, {
                variant: 'success',
            });
            fetchPreCreateUpdateData();
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const handleChange = (id, isChecked, type) => {
        let roles = [...selectedRoles[type]];
        if (!isChecked) roles.splice(roles.indexOf(id), 1);
        else roles = [...roles, id];
        setSelectedRoles({ ...selectedRoles, [type]: roles });
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Review Policy Roles</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <Grid container direction="column">
                            <Grid container direction="flex" spacing={4} p={3}>
                                <Grid
                                    item
                                    sx={{
                                        width: '50%',
                                    }}
                                    p={2}
                                >
                                    <DisplayChangeRequestPolicyRoles
                                        title="Reviewer Roles"
                                        isLoading={isLoading}
                                        roles={roles?.reviewer?.map((item) => ({
                                            id: item.id,
                                            label: item.displayName,
                                        }))}
                                        selectedRoles={selectedRoles.reviewer}
                                        handleChange={(id, isChecked) => handleChange(id, isChecked, 'reviewer')}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    sx={{
                                        width: '50%',
                                    }}
                                    p={2}
                                >
                                    <DisplayChangeRequestPolicyRoles
                                        title="Approval Roles"
                                        isLoading={isLoading}
                                        roles={roles?.approver?.map((item) => ({
                                            id: item.id,
                                            label: item.displayName,
                                        }))}
                                        selectedRoles={selectedRoles.approver}
                                        handleChange={(id, isChecked) => handleChange(id, isChecked, 'approver')}
                                    />
                                </Grid>
                            </Grid>
                            <PermissionChecker
                                requiredPermissions={['ROLE_BASE_PERMISSION_PRE_HQ_SAVE_REVIEW_POLICY_ROLES']}
                                noAuthRedirect={false}
                            >
                                <Grid
                                    item
                                    sx={{
                                        pt: '0 !important',
                                        display: 'flex',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flexGrow: 1,
                                        }}
                                    />
                                    <LoadingButton
                                        loading={isSaveLoading}
                                        variant="contained"
                                        color="primary"
                                        disabled={
                                            selectedRoles.approver.length === 0 || selectedRoles.reviewer.length === 0
                                        }
                                        onClick={updateRoles}
                                    >
                                        Update
                                    </LoadingButton>
                                </Grid>
                            </PermissionChecker>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default ChangeRequestPolicyRoles;
